// client/src/pages/business/FlashOffersFeed.js
// ================================================================
// BUSINESS VIEW: Browse active flash offers from their wholesalers
// ================================================================

import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  Zap, Clock, Tag, ShoppingCart, ChevronRight,
  AlertTriangle, Package, ExternalLink, Check, SlidersHorizontal
} from 'lucide-react';
import Navbar from '../../components/common/Navbar';
import StorefrontBanner from '../../components/common/StorefrontBanner';
import Footer from '../../components/common/Footer';
import axios from 'axios';
import { API_URL, getAuthHeaders } from '../../services/api';
import { useTranslation } from 'react-i18next';
import { useCart } from '../../contexts/CartContext';

const FlashOffersFeed = () => {
  const { t } = useTranslation(['business', 'common']);
  const navigate = useNavigate();
  const [offers, setOffers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const { addToCart } = useCart();
  const [addedItems, setAddedItems] = useState({});
  const [sortBy, setSortBy] = useState('timeRemaining');
  const [filterWholesaler, setFilterWholesaler] = useState('all');
  const [quantities, setQuantities] = useState({});

  const getQty = (item) => quantities[item.productId] ?? (item.minOrderQuantity || 1);

  const adjustQty = (item, delta) => {
    const step = item.minOrderQuantity || 1;
    const current = getQty(item);
    const next = Math.max(step, current + delta * step);
    setQuantities(prev => ({ ...prev, [item.productId]: next }));
  };

  const handleAddToCart = (item, offer) => {
    const qty = getQty(item);
    addToCart({
      _id: item.productId,
      name: item.productName,
      category: item.category,
      images: item.image ? [item.image] : [],
      pricing: {
        unitPrice: item.originalPrice,
        unit: item.unit || 'τεμ',
        minOrderQuantity: item.minOrderQuantity || 1,
      },
      wholesalerId: offer.wholesaler?._id,
      wholesaler: offer.wholesaler?.companyName,
      // Carry hidePricing so cart respects wholesaler pricing visibility
      hidePricing: offer.wholesaler?.hidePricing || false,
      // ⚡ Flash offer data
      flashOfferPrice: item.offerPrice,
      flashOfferId: offer._id,
      flashOfferTitle: offer.title,
    }, qty);
    setAddedItems(prev => ({ ...prev, [item.productId]: true }));
    setTimeout(() => setAddedItems(prev => ({ ...prev, [item.productId]: false })), 2000);
  };

  useEffect(() => {
    fetchOffers();
  }, []);

  const fetchOffers = async () => {
    try {
      setLoading(true);
      const res = await axios.get(`${API_URL}/flash-offers/available`, {
        headers: getAuthHeaders()
      });
      setOffers(res.data.offers || []);
    } catch (err) {
      console.error('Fetch offers error:', err);
      setError(err.response?.data?.message || t('flashOffers.failedToLoadOffers'));
    } finally {
      setLoading(false);
    }
  };

  const formatTimeRemaining = (endDate) => {
    const remaining = new Date(endDate) - new Date();
    if (remaining <= 0) return t('business:flashOffers.ended', 'Ended');
    const hours = Math.floor(remaining / (1000 * 60 * 60));
    const days = Math.floor(hours / 24);
    if (days > 0) return `${days}d ${hours % 24}h`;
    const mins = Math.floor((remaining % (1000 * 60 * 60)) / (1000 * 60));
    if (hours > 0) return `${hours}h ${mins}m`;
    return `${mins}m`;
  };

  const isUrgent = (endDate) => {
    const remaining = new Date(endDate) - new Date();
    return remaining < 24 * 60 * 60 * 1000; // less than 24h
  };

  const wholesalerOptions = useMemo(() => {
    const map = new Map();
    offers.forEach(o => {
      if (o.wholesaler?._id) map.set(o.wholesaler._id, o.wholesaler.companyName);
    });
    return [...map.entries()];
  }, [offers]);

  const displayOffers = useMemo(() => {
    let result = offers;
    if (filterWholesaler !== 'all') {
      result = result.filter(o => o.wholesaler?._id === filterWholesaler);
    }
    return [...result].sort((a, b) => {
      if (sortBy === 'discount') return (b.discountPercentage || 0) - (a.discountPercentage || 0);
      return (new Date(a.endDate) - new Date()) - (new Date(b.endDate) - new Date());
    });
  }, [offers, filterWholesaler, sortBy]);

  return (
    <div className="min-h-screen bg-gray-50">
      <Navbar />
      <StorefrontBanner />
      <div className="max-w-6xl mx-auto px-4 sm:px-6 py-6 sm:py-8">
        {/* Header */}
        <div className="mb-6">
          <h1 className="text-2xl sm:text-3xl font-bold text-gray-900 flex items-center gap-2">
            <Zap className="w-7 h-7 text-red-500" />
            {t('business:flashOffers.title', 'Flash Offers')}
          </h1>
          <p className="text-gray-500 mt-1">
            {t('business:flashOffers.subtitle', 'Special time-limited deals from your wholesalers')}
          </p>
        </div>

        {/* Filter / Sort bar */}
        {offers.length > 0 && (
          <div className="flex flex-wrap items-center gap-3 mb-6 bg-white rounded-lg border border-gray-200 p-3">
            <SlidersHorizontal className="w-4 h-4 text-gray-400" />
            <select
              value={filterWholesaler}
              onChange={e => setFilterWholesaler(e.target.value)}
              className="text-sm border border-gray-300 rounded-lg px-3 py-1.5 bg-white"
            >
              <option value="all">{t('business:flashOffers.allWholesalers', 'All Wholesalers')}</option>
              {wholesalerOptions.map(([id, name]) => (
                <option key={id} value={id}>{name}</option>
              ))}
            </select>
            <select
              value={sortBy}
              onChange={e => setSortBy(e.target.value)}
              className="text-sm border border-gray-300 rounded-lg px-3 py-1.5 bg-white"
            >
              <option value="timeRemaining">{t('business:flashOffers.sortTimeRemaining', 'Ending Soonest')}</option>
              <option value="discount">{t('business:flashOffers.sortDiscount', 'Highest Discount')}</option>
            </select>
          </div>
        )}

        {/* Error */}
        {error && (
          <div className="bg-red-50 border border-red-200 text-red-700 rounded-lg p-4 mb-6">{error}</div>
        )}

        {/* Loading */}
        {loading ? (
          <div className="text-center py-16 text-gray-400">
            <div className="animate-spin w-8 h-8 border-2 border-red-500 border-t-transparent rounded-full mx-auto mb-4"></div>
            {t('common:loading', 'Loading...')}
          </div>
        ) : displayOffers.length === 0 ? (
          <div className="text-center py-16 bg-white rounded-xl border border-gray-200">
            <Tag className="w-16 h-16 text-gray-200 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-gray-600 mb-2">
              {t('business:flashOffers.noOffers', 'No active offers right now')}
            </h3>
            <p className="text-gray-400">
              {t('business:flashOffers.noOffersDesc', 'Check back later — your wholesalers may post new deals soon.')}
            </p>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {displayOffers.map(offer => (
              <div
                key={offer._id}
                className={`bg-white rounded-xl border overflow-hidden shadow-sm hover:shadow-md transition-shadow ${
                  isUrgent(offer.endDate) ? 'border-red-300 ring-1 ring-red-100' : 'border-gray-200'
                }`}
              >
                {/* Offer header gradient */}
                <div className="bg-gradient-to-r from-red-600 to-orange-500 px-5 py-3 flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <Zap className="w-5 h-5 text-white" />
                    <span className="text-white font-bold text-lg">
                      {offer.wholesaler?.hidePricing
                        ? t('business:flashOffers.specialOffer', 'SPECIAL OFFER')
                        : offer.discountType === 'percentage' ? `${offer.discountPercentage}% OFF` : 'SPECIAL PRICE'}
                    </span>
                  </div>
                  <div className={`flex items-center gap-1 text-sm font-medium ${
                    isUrgent(offer.endDate) ? 'text-yellow-200 animate-pulse' : 'text-red-100'
                  }`}>
                    <Clock className="w-4 h-4" />
                    {formatTimeRemaining(offer.endDate)}
                  </div>
                </div>

                {/* Content */}
                <div className="p-5">
                  <h3 className="font-bold text-gray-900 text-lg mb-1">{offer.title}</h3>
                  {offer.description && (
                    <p className="text-sm text-gray-500 mb-3 line-clamp-2">{offer.description}</p>
                  )}

                  {/* Wholesaler */}
                  <div className="flex items-center gap-2 mb-4">
                    {offer.wholesaler?.logo ? (
                      <img src={offer.wholesaler.logo} alt="" className="w-6 h-6 rounded-full object-cover" />
                    ) : (
                      <div className="w-6 h-6 rounded-full bg-blue-100 flex items-center justify-center">
                        <Package className="w-3.5 h-3.5 text-blue-600" />
                      </div>
                    )}
                    <span className="text-sm font-medium text-gray-700">{offer.wholesaler?.companyName}</span>
                  </div>

                  {/* Product list */}
                  <div className="space-y-2 mb-4">
                    {offer.items?.slice(0, 4).map((item, idx) => (
                      <div key={idx} className="flex items-center justify-between py-1.5">
                        <div className="flex items-center gap-2 min-w-0">
                          {item.image && <img src={item.image} alt="" className="w-8 h-8 rounded object-cover flex-shrink-0" />}
                          <span className="text-sm text-gray-700 truncate">{item.productName}</span>
                        </div>
                        <div className="flex items-center gap-2 flex-shrink-0 ml-2">
                          {!offer.wholesaler?.hidePricing && (
                            <>
                              <span className="text-xs text-gray-400 line-through">€{item.originalPrice?.toFixed(2)}</span>
                              <span className="text-sm font-bold text-red-600">€{item.offerPrice?.toFixed(2)}</span>
                            </>
                          )}
                          {/* Qty controls */}
                          <div className="flex items-center gap-1">
                            <button
                              onClick={(e) => { e.stopPropagation(); adjustQty(item, -1); }}
                              className="w-5 h-5 rounded bg-gray-100 hover:bg-gray-200 text-gray-600 flex items-center justify-center text-xs font-bold"
                              disabled={getQty(item) <= (item.minOrderQuantity || 1)}
                            >−</button>
                            <span className="text-xs font-medium text-gray-700 w-6 text-center">{getQty(item)}</span>
                            <button
                              onClick={(e) => { e.stopPropagation(); adjustQty(item, 1); }}
                              className="w-5 h-5 rounded bg-gray-100 hover:bg-gray-200 text-gray-600 flex items-center justify-center text-xs font-bold"
                            >+</button>
                          </div>
                          <button
                            onClick={(e) => { e.stopPropagation(); handleAddToCart(item, offer); }}
                            className={`w-6 h-6 rounded flex items-center justify-center text-xs font-medium transition-all ${
                              addedItems[item.productId]
                                ? 'bg-green-100 text-green-700'
                                : 'bg-red-100 text-red-600 hover:bg-red-200'
                            }`}
                            title={addedItems[item.productId] ? 'Added!' : 'Add to cart'}
                          >
                            {addedItems[item.productId] ? <Check className="w-3 h-3" /> : <ShoppingCart className="w-3 h-3" />}
                          </button>
                        </div>
                      </div>
                    ))}
                    {offer.items?.length > 4 && (
                      <p className="text-xs text-gray-400 text-center">
                        + {offer.items.length - 4} {t('business:flashOffers.moreProducts', 'more products')}
                      </p>
                    )}
                  </div>

                  {/* Minimum order */}
                  {offer.minimumOrderAmount > 0 && (
                    <div className="text-xs text-amber-600 bg-amber-50 rounded px-2 py-1 mb-3 flex items-center gap-1">
                      <AlertTriangle className="w-3 h-3" />
                      {t('business:flashOffers.minOrder', 'Minimum order')}: €{offer.minimumOrderAmount}
                    </div>
                  )}

                  {/* CTA */}
                  <div className="flex gap-2">
                    <button
                      onClick={() => {
                        offer.items?.forEach(item => handleAddToCart(item, offer));
                      }}
                      className="flex-1 flex items-center justify-center gap-2 bg-red-600 hover:bg-red-700 text-white py-2.5 rounded-lg font-medium transition-colors"
                    >
                      <ShoppingCart className="w-4 h-4" />
                      {t('business:flashOffers.addAllToCart', 'Add All to Cart')}
                    </button>
                    <button
                      onClick={() => navigate(`/wholesalers/${offer.wholesaler?._id}`)}
                      className="px-4 flex items-center justify-center bg-gray-100 hover:bg-gray-200 text-gray-700 py-2.5 rounded-lg transition-colors"
                      title={t('business:flashOffers.browseCatalog', 'Browse Catalog')}
                    >
                      <ChevronRight className="w-5 h-5" />
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
      <Footer />
    </div>
  );
};

export default FlashOffersFeed;